<template>
  <div :class="isIntegration ? '' : 'keno'">
    <router-view />
    <Modal
      v-if="showFreeBetModal"
      ref="modal"
      class="free-bet-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="claimFreeBets()"
      @close-modal-outside="claimFreeBets('outside')"
    >
      <template #body>
        <div class="body">
          <img src="@/assets/images/free-bet-star.png" alt="" />
          <div class="body-message-box">
            <div class="pre-title">
              {{ translations.general_free_bet_pre_title.toUpperCase() }}
            </div>
            <div class="title">
              {{ translations.general_free_bet }}
            </div>
            <div class="subtitle">
              {{ translations.general_congratulation.toUpperCase() }}
            </div>
            <span class="subtitle-2">{{
              translations.general_claim_ticket
            }}</span>
          </div>
          <div class="action">
            <button class="button" @click="claimFreeBets">
              {{ claimFreeBetsLabel }}
            </button>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetExpiredModal"
      ref="modal"
      class="free-bet-expired-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="toggleFreeBetExpiredModal(false)"
      @close-modal-outside="toggleFreeBetExpiredModal('outside')"
    >
      <template #header>
        <h3>
          {{ translations.general_info }}
        </h3>
      </template>
      <template #body>
        <div>
          {{ freeBetExpiredMessage }}
        </div>
      </template>
      <template #footer>
        <div>
          <button class="button" @click="toggleFreeBetExpiredModal(false)">
            {{ translations.general_ok }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import helpers from '@nsftx/games-sdk-js/src/utility/helpers';
import gtag from '@nsftx/games-sdk-js/src/utility/gtag';
import applyPalette from '@nsftx/games-sdk-js/src/utility/applyPalette';
import { BusService } from '@nsftx/games-bus';
import { isNil, clone, includes, toArray, startCase, merge } from 'lodash';
import api from '@/api';
import Modal from '@nsftx/games-sdk-js/src/components/Modal';

import { mapRebet } from '../utility';

export default {
  name: 'Keno',
  components: {
    Modal,
  },
  data() {
    return {
      isAppReady: false,
      getResultsTimeoutId: null,
      instance: getCurrentInstance(),
      userFreeBets: {
        count: 0,
        betStake: 0,
      },
    };
  },
  computed: {
    ...mapState([
      'config',
      'checkTicketTimeoutId',
      'isCompactLayout',
      'jackpotState',
      'igniteInstance',
      'layout',
      'userUuid',
      'terminalUserData',
      'terminalTicketDetails',
      'visualizationState',
      'activeComponent',
      'showFreeBetModal',
      'showFreeBetExpiredModal',
      'nextgenStandaloneIntegration',
    ]),
    ...mapGetters('user', ['isLoggedIn', 'user']),
    ...mapGetters([
      'getTranslation',
      'isMobile',
      'isTablet',
      'ui',
      'isDesktop',
      'isTerminalChannel',
      'isTerminalLayout',
      'userIsOperator',
      'ajsDisabled',
      'translations',
      'customDomain',
    ]),
    isIntegration() {
      return this.ui?.config?.mode === 'integration';
    },
    isStandalone() {
      return this.ui?.config?.mode === 'standalone';
    },
    isGravityGateway() {
      return this.config.messageProtocol.name === 'gravityGateway';
    },
    claimFreeBetsLabel() {
      if (this.isMobile) {
        return this.translations.general_claim;
      }
      return this.translations.general_ok;
    },
    freeBetExpiredMessage() {
      return this.translations.general_free_bet_expired_message;
    },
  },
  watch: {
    isLoggedIn(value, oldValue) {
      if (value) {
        this.getLastTickets();
      }

      if (oldValue && !value) {
        this.setCurrentBetType({ label: 'No Bet' });
        this.addPreBet({
          bet: '',
          valid: false,
        });
        this.setSelectedNumbers([]);
      }
    },
  },
  mounted() {
    this.sendLoadedEvent();
    this.setPageTitle();

    if (this.isLoggedIn) {
      this.getLastTickets();
    }

    if (this.isTerminalChannel) {
      this.$busService.addChannel('Device', this.config.device.uuid);
    }
    eventBus.$on('TicketCancelled', async (id) => {
      this.setCancelMessage(id);
      this.setIsCancelMessageOpen(true);
      this.getLastTickets();
      setTimeout(async () => {
        if (this.nextgenStandaloneIntegration) {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }
      }, 1000);
    });
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    this.$busService.removeChannel('Jackpot', this.jackpotState.jackpotId);

    clearTimeout(this.getResultsTimeoutId);

    if (this.isTerminalChannel) {
      this.$busService.removeChannel('Device', this.config.device.uuid);
    }

    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeMount() {
    this.setHTMLLangAttribute();
    this.setTheme(this.config);
    this.initBusService();

    if (!this.ajsDisabled) {
      (async () => {
        await this.setJackpotState();

        if (this.jackpotState) {
          this.$busService.addChannel('Jackpot', this.jackpotState.jackpotId);
        }
      })();
    }

    this.setTheme(this.config);

    const device = !this.isDesktop ? 'mobile' : 'desktop';

    this.setupGtag(device);

    eventBus.$on('ReturnToLobby', () => {
      this.$busService.sendMessage(
        {
          action: 'UI.Show',
          data: {
            name: 'Lobby',
          },
        },
        true,
      );

      if (window.WebAppListener) {
        window.WebAppListener.sendMessage('Navigation.Back');
      }
    });
  },
  methods: {
    ...mapActions('gamesBetslip', [
      'addPreBet',
      'clearBetslip',
      'cancelTicket',
      'getLastTickets',
      'rebet',
      'setIsPayinInProgress',
      'setIsLastTicketsInProgress',
      'setIsPayinButtonDisabled',
    ]),
    ...mapActions('user', [
      'setUserBalance',
      'getUserBalance',
      'setUserFreeBets',
      'getUserFreeBets',
    ]),
    ...mapActions('notifications', ['setNotification', 'clearNotification']),
    ...mapActions([
      'getStatistics',
      'getResults',
      'getTicketDetails',
      'setGameState',
      'setIsMobile',
      'setOdds',
      'setConfig',
      'setIsPayinInProcess',
      'setIsCancelMessageOpen',
      'setCurrentBetType',
      'setSelectedNumbers',
      'setCancelMessage',
      'setUserUuid',
      'setVisualizationState',
      'setDeviceLayout',
      'setJackpotState',
      'setTerminalTicketsHistory',
      'setTerminalTicketDetails',
      'setNextRoundDate',
      'setBettingDisabled',
      'updateConfig',
      'toggleFreeBetModal',
      'toggleFreeBetExpiredModal',
    ]),
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        if (this.isMobile || this.isTablet) {
          if (this.isLoggedIn) {
            this.getLastTickets();
          }
        }
      }
    },
    initBusService() {
      const busService = new BusService({
        environment: this.config.environment,
        platform: this.config.platform,
        platformName: this.config.platformName,
        productName: this.config.productName,
        productId: this.config.productId,
        tenantId: this.config.tenantId,
        locale: this.config.locale,
        applicationName: this.config.applicationName,
        clientMessageProtocol: this.config.messageProtocol,
        bounceToFrames: [{ frameId: 'kenoVisualization' }],
        adapters: [
          {
            name: 'GcmAdapter',
          },
        ],
        customDomain: this.customDomain,
      });

      eventBus.$on(
        'Keno:AuthorizationChanged',
        ({ isUserLoggedIn, userUuid }) => {
          if (isUserLoggedIn) {
            this.setUserUuid(userUuid);
            busService.addChannel('Player', userUuid);
            if (window.WebAppListener) {
              this.getUserFreeBets();
            }
          } else {
            busService.removeChannel('Player', this.userUuid);
            this.setUserUuid(null);
          }
        },
      );
      // Add bus service to Vue instance so we can use it throughout the project.
      this.instance.appContext.config.globalProperties.$busService = busService;

      this.setBusListeners();

      busService.start();
    },
    sendLoadedEvent() {
      if (window.WebAppListener) {
        window.WebAppListener.sendMessage('Slave.Loaded');
      }
      this.$busService.sendMessage(
        {
          action: 'Slave.Loaded',
          data: {},
        },
        true,
      );
    },
    setTheme(config) {
      const rootElement = document.documentElement;
      const baseColor = config.ui[config.ui.config.baseColor];
      const theme = config.ui[config.ui.config.theme];
      applyPalette.set(merge(theme, baseColor), rootElement);
    },
    setHTMLLangAttribute() {
      document.querySelector('html').setAttribute('lang', this.config.locale);
    },
    setupGtag(device) {
      if (this.config.ui.config.gtm) {
        const { code, auth, preview } = this.config.ui.config.gtm[device];

        gtag.setup(code, auth, preview);

        return;
      }

      gtag.setup();
    },
    setBusListeners() {
      window.addEventListener('Bus:SetState', (event) => {
        const { detail } = event;

        if (detail.eventId) {
          this.setGameState(detail);
          this.setVisualizationState(detail);
          this.setOdds(detail.odds);
          this.setNextRoundDate(detail.nextRound.startDate.date);

          eventBus.$emit('SetStateInit');

          if (this.isLoggedIn) {
            // Check if user has a free bets
            this.getUserFreeBets();
          }
        }
      });

      window.addEventListener('Bus:SetConfiguration', (event) => {
        const { detail } = event;

        this.setGameState(detail);
        this.setOdds(detail.odds);
      });

      window.addEventListener('Bus:StartCountdown', (event) => {
        eventBus.$emit('SetRoundInProgress', false);
        this.setGameState(event.detail);
        this.setNextRoundDate(event.detail.nextRound.startDate.date);

        if (this.isLoggedIn) {
          this.getLastTickets();
          setTimeout(async () => {
            if (this.nextgenStandaloneIntegration) {
              await window.igniteInstance.getWallets().then((result) => {
                if (result.activeCurrencyBalance) {
                  this.setUserBalance(result.activeCurrencyBalance);
                }
              });
            }
          }, 1000);
          if (this.isStandalone && !this.isGravityGateway) {
            this.getUserBalance();
          }
        }

        if (this.isTerminalLayout) {
          if (this.terminalTicketDetails?.id) {
            this.getTicketDetails({
              terminalUserData: this.terminalUserData,
              id: this.terminalTicketDetails.id,
              busServiceCallback: this.$busService,
            });
          }
        }
      });

      window.addEventListener('Bus:StopBetting', (event) => {
        this.setGameState(event.detail);
      });

      window.addEventListener('Bus:StartEvent', (event) => {
        this.setGameState(event.detail);
      });

      window.addEventListener('Bus:DrawResult', (event) => {
        const currentBallEventId = event.detail.eventId;
        const currentBallId = event.detail.id;
        const currentBalls = clone(this.visualizationState.balls);
        const currentBallsArray = toArray(currentBalls);
        const isCurrentBallMissing = !includes(currentBalls, currentBallId);

        // While visualization loads it could take a while some
        // balls could be missing because SetState event came earlier
        // and visualization didn't load yet
        // We need to add missing balls to the visualization state
        // so when visualization loads it has all balls needed
        if (isCurrentBallMissing) {
          if (!isNil(currentBalls)) {
            currentBalls[currentBallsArray.length + 1] = event.detail.ball;

            this.setVisualizationState({
              ...this.visualizationState,
              ball: event.detail.ball,
              balls: currentBalls,
              eventId: currentBallEventId,
              eventName: event.detail.eventName,
              sentTime: event.detail.sentTime,
            });
          }
        }

        // Wait for the first card to get drawn
        // to make sure our open tickets are actually in play
        if (event.detail.id === 1) {
          if (this.isLoggedIn) {
            this.getLastTickets();
            if (this.isStandalone && !this.isGravityGateway) {
              this.getUserBalance();
            }
          }

          if (this.isTerminalLayout) {
            if (this.terminalTicketDetails?.id) {
              this.getTicketDetails({
                terminalUserData: this.terminalUserData,
                id: this.terminalTicketDetails.id,
                busServiceCallback: this.$busService,
              });
            }
          }
        }

        this.setGameState(event.detail);
      });

      window.addEventListener('Bus:SetResults', (event) => {
        this.setGameState(event.detail);
        this.getStatistics();

        clearTimeout(this.getResultsTimeoutId);

        this.getResultsTimeoutId = setTimeout(() => {
          this.getResults();
        }, 5000);
      });

      window.addEventListener('Bus:User.SettingsChanged', () => {
        if (!this.isLoggedIn) {
          this.resetUserFreeBets();
          this.clearBetslip();
          this.setUserFreeBets(this.userFreeBets);
        } else {
          this.getUserFreeBets();
        }
      });

      window.addEventListener('Bus:User.BalanceChanged', (event) => {
        const {
          detail: {
            data: { balance },
          },
        } = event;

        this.setUserBalance(balance);
      });

      window.addEventListener('Bus:Tickets.FetchHistory', async (event) => {
        const {
          detail: { data },
        } = event;

        try {
          const response = await api.ticketsHistory.getTicketsHistory(
            this.terminalUserData,
            this.config,
            data.timeFrom,
            data.timeTo,
            data.count,
          );

          this.setTerminalTicketsHistory(response.message);
        } catch (error) {
          const message = error?.response?.data?.message ?? 'unknown_error';

          this.$busService.sendMessage(
            {
              action: 'Dialog.Show',
              data: {
                message: this.getTranslation(message),
                type: 'warning',
                delay: 3000,
              },
            },
            true,
          );
        }
      });

      window.addEventListener('Bus:Tickets.Checked', (event) => {
        const { detail } = event;

        this.getTicketDetails({
          terminalUserData: this.terminalUserData,
          id: detail.data.ticket.id,
          busServiceCallback: this.$busService,
        });
      });

      window.addEventListener('Bus:TicketUpdate', (event) => {
        const {
          detail: { code },
        } = event;

        const {
          detail: { action },
        } = event;

        const actions = {};
        const ticket = event.detail;

        this.$busService.sendMessage(
          {
            action: 'Tickets.Resolved',
            data: {
              accepted: true,
              ticket: event.detail,
            },
          },
          true,
        );

        const ticketAction = action.charAt(0).toUpperCase() + action.slice(1);
        this.$busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: ticketAction,
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );

        actions.add = async () => {
          // Cancel check.json if ticketUpdate is recieved
          clearTimeout(this.checkTicketTimeoutId);

          if (!isNil(code)) {
            const message = event?.detail?.message ?? 'unknown_error';

            eventBus.$emit('SwitchToBetslipTab');
            this.setNotification({
              message,
              status: 'negative',
              notificationTimeout: null,
            });

            if (this.isTerminalLayout) {
              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation(message),
                    type: 'warning',
                    delay: 3000,
                  },
                },
                true,
              );
            }
          } else {
            await this.setCurrentBetType({ label: 'No Bet' });
            await this.clearBetslip();
            await this.addPreBet({
              bet: '',
              valid: false,
            });
            await this.setSelectedNumbers([]);

            eventBus.$emit('SwitchToBetslipTab');
            this.setNotification({
              local: 'general_ticket_accepted',
              status: 'positive',
              notificationTimeout: 3000,
            });

            if (this.isLoggedIn && !this.isTerminalChannel) {
              this.getLastTickets();
              this.getUserFreeBets();
              if (this.isStandalone && !this.isGravityGateway) {
                this.getUserBalance();
              }
            }

            if (this.isTerminalChannel) {
              ticket.bets.forEach((bet) => {
                if (bet.system) {
                  // eslint-disable-next-line no-param-reassign
                  bet.totalCombinations = helpers.getCombinations(
                    bet.value.length,
                    bet.system,
                  );
                  // eslint-disable-next-line no-param-reassign
                  bet.stakePerCombination = bet.amount / bet.totalCombinations;
                }
              });

              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation('ticket.add_success'),
                    type: 'success',
                    delay: 3000,
                  },
                },
                true,
              );

              this.$busService.sendMessage(
                {
                  action: 'Peripherals.Print',
                  enforceEvent: true,
                  data: {
                    type: 'ticket',
                    productId: this.config.productName,
                    data: ticket,
                  },
                },
                true,
              );
            }
          }

          this.setIsPayinInProcess(false);
          this.setIsPayinInProgress(false);
        };

        actions.cancel = () => {
          if (!isNil(code)) {
            const message = event?.detail?.message ?? 'unknown_error';
            this.setIsLastTicketsInProgress(false);

            if (!this.isTerminalLayout) {
              eventBus.$emit('SwitchToBetslipTab');
              this.setNotification({
                message,
                status: 'negative',
                notificationTimeout: null,
              });
            }

            if (this.isTerminalLayout) {
              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation(message),
                    type: 'warning',
                    delay: 3000,
                  },
                },
                true,
              );
            }
          } else {
            eventBus.$emit('RefreshTicketHistory');

            if (this.isLoggedIn && !this.isTerminalChannel) {
              this.getLastTickets();
              if (this.isStandalone && !this.isGravityGateway) {
                this.getUserBalance();
              }
            }

            if (this.isTerminalLayout) {
              // Cancel check.json if ticketUpdate is recieved
              clearTimeout(this.checkTicketTimeoutId);

              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation(
                      'ticket.cancel_success_to_balance',
                    ).replace(
                      '{{amount}}',
                      event.detail.cancelledAmount.toFixed(2),
                    ),
                    type: 'success',
                    delay: 3000,
                  },
                },
                true,
              );

              this.setTerminalTicketDetails(ticket);
            }

            this.setCancelMessage(event.detail?.id);
            this.setIsCancelMessageOpen(true);
          }
        };

        actions.payout = () => {
          if (!isNil(code)) {
            const message = event?.detail?.message ?? 'unknown_error';

            eventBus.$emit('SwitchToBetslipTab');
            this.setNotification({
              message,
              status: 'negative',
              notificationTimeout: null,
            });

            if (this.isTerminalLayout) {
              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation(message),
                    type: 'warning',
                    delay: 3000,
                  },
                },
                true,
              );
            }
          } else {
            // eslint-disable-next-line no-lonely-if
            if (this.isTerminalLayout) {
              // Cancel check.json if ticketUpdate is recieved
              clearTimeout(this.checkTicketTimeoutId);

              if (this.userIsOperator) {
                ticket.possibleWinnings = ticket.winnings;
                ticket.possiblePayout = ticket.payout;

                this.$busService.sendMessage(
                  {
                    action: 'Peripherals.Print',
                    enforceEvent: true,
                    data: {
                      layoutName: 'ticketKenoPayout',
                      productId: this.config.productName,
                      data: ticket,
                    },
                  },
                  true,
                );
              }

              this.$busService.sendMessage(
                {
                  action: 'Dialog.Show',
                  data: {
                    message: this.getTranslation(
                      'ticket.payout_success_to_balance',
                    ).replace('{{amount}}', event.detail.payout.toFixed(2)),
                    type: 'success',
                    delay: 3000,
                  },
                },
                true,
              );

              if (this.terminalTicketDetails?.id) {
                this.getTicketDetails({
                  terminalUserData: this.terminalUserData,
                  id: this.terminalTicketDetails.id,
                  busServiceCallback: this.$busService,
                });
              }
            }
          }
        };

        actions[action]();
      });

      window.addEventListener('Bus:onSettingsUpdated', (event) => {
        const { data } = event.detail;
        const { activeLocale, activeCurrency } = data;

        if (this.config.locale !== activeLocale) {
          this.updateConfig({ locale: activeLocale });
        }

        if (this.config.currency !== activeCurrency.code) {
          this.updateConfig({ currency: activeCurrency.code });
        }
      });

      window.addEventListener('Bus:onBettingDenied', () => {
        this.setNotification({
          local: 'general_betting_disabled_warning',
          status: 'negative',
        });

        this.setIsPayinButtonDisabled(true);
        this.setBettingDisabled(true);
      });

      window.addEventListener('Bus:onBettingAllowed', () => {
        this.clearNotification();

        this.setIsPayinButtonDisabled(false);
        this.setBettingDisabled(false);
      });

      window.addEventListener('Bus:onCancelBetsRequested', async (event) => {
        const {
          detail: { data },
        } = event;

        await this.cancelTicket({
          id: data.betIds[0],
          providerBetslipId: data.providerBetslipId,
        });

        this.getLastTickets();
      });

      window.addEventListener('Bus:onReBetRequested', (event) => {
        const {
          detail: { data },
        } = event;

        api.ticketCheck
          .checkTicketByProviderBetslipId(
            data.providerBetslipId,
            this.config,
            this.user,
          )
          .then((response) => {
            const ticket = response.data.message;
            const bets = mapRebet(ticket);
            this.rebet({ bets, payin: ticket.payin }).then(() => {
              eventBus.$emit('TicketRebet');
            });
          });
      });
      window.addEventListener('Bus:freebet', (event) => {
        this.userFreeBets = event.detail;
        // eslint-disable-next-line no-case-declarations
        const { subtype } = event.detail;
        if (subtype === 'new') {
          this.toggleFreeBetModal(true);
        } else {
          if (this.user.freeBets.count > 0) {
            this.toggleFreeBetExpiredModal(true);
          }
          this.getUserFreeBets();
        }
      });
    },
    setPageTitle() {
      const { productFqn, applicationName } = this.config;
      document.title = `${startCase(productFqn)}: ${applicationName}`;
    },
    claimFreeBets() {
      this.clearBetslip();
      this.setUserFreeBets(this.userFreeBets);
      this.toggleFreeBetModal(false);
    },
    resetUserFreeBets() {
      this.userFreeBets = {
        count: 0,
        betStake: 0,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.keno {
  height: 100vh;
}

.free-bet-expired-modal {
  &::v-deep .modal {
    height: 182px;
  }
}

.free-bet-expired-modal,
.free-bet-modal {
  &::v-deep .modal {
    background-color: var(--card);

    .modal-header,
    .modal-body {
      color: var(--text-primary-1);
    }

    .modal-header {
      font-size: 16px;
      padding: 0 16px;
    }
    .modal-body {
      font-size: 14px;
      .modal-actions {
        display: none;
      }
    }
    .modal-footer {
      padding-right: 43px;
    }
    .button {
      background: transparent;
      border: 0;
      outline: 0;
      font-size: 14px;
      color: var(--secondary);
      text-transform: uppercase;
      cursor: pointer;
      font-family: Roboto, 'sans-serif';
      padding: 31px 0 27px 0;
    }
  }
}

.free-bet-modal {
  &::v-deep .modal {
    max-height: 348px !important;
    .modal-header {
      display: none;
    }
    .modal-body {
      height: 348px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      background-position: 0 0;
      background-repeat: no-repeat;
      position: relative;
      text-align: center;
      .body {
        height: 100%;
        img {
          position: absolute;
          top: -2%;
          left: 22px;
          max-width: 100%;
        }
        .body-message-box {
          position: absolute;
          width: 100%;
          top: 66%;
          left: 50%;
          transform: translate(-50%, -50%);

          .title {
            font-size: 42px;
            font-weight: 700;
            color: #90b653;
            margin-bottom: 25px;
          }
          .subtitle {
            font-size: 18px;
            font-weight: 700;
          }
          .subtitle-2 {
            font-size: 14px;
            color: var(--text-primary-3);
          }
        }
      }
    }
    .modal-footer {
      display: none;
    }
  }
  &.desktop {
    &::v-deep .modal {
      .modal-body {
        background-image: none;
        .body {
          img {
            height: 60%;
            left: 50%;
            top: -2%;
            transform: translate(-50%, 0);
          }
          .body-message-box {
            top: 60%;
            .title {
              margin-bottom: 10px;
            }
            .pre-title {
              display: none;
            }
          }
          .action {
            position: absolute;
            bottom: 0;
            right: 16px;
          }
        }
      }
    }
  }
  &.mobile {
    &::v-deep .modal {
      max-height: 70% !important;
      .modal-body {
        background-image: url('../assets/images/free-bet-bg.svg');
        height: 100% !important;
        .body {
          .body-message-box {
            .title {
              text-transform: uppercase;
            }
          }
          .action {
            position: absolute;
            left: 0;
            bottom: 20px;
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
            .button {
              min-width: 150px;
              background: #90b653;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 30px;
              padding: 0;
              color: var(--text-secondary-1);
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}
</style>
